/*!
 * Fresh Coast — Navigation, hero, and scroll storytelling
 * Requires: GSAP 3 (gsap.min.js + ScrollTrigger.min.js before this file)
 * Respects: prefers-reduced-motion
 */

(function () {
  'use strict';

  /* Every selector that CSS pre-hides for a JS-driven reveal. If GSAP or
     ScrollTrigger never arrives, these must be force-shown or the page
     loads with permanently invisible content. */
  var FC_PREHIDDEN_SELECTORS =
    '.fc-hero-anim, .fc-reveal, .fc-collection-hero-anim, [data-fc-ethos-copy], [data-fc-ethos-graphic], [data-fc-card-media], [data-fc-card-copy], [data-fc-split-copy], [data-fc-locator-copy], [data-fc-locator-embed], [data-fc-locator-footer], [data-fc-retailers-copy], [data-fc-headline-reveal] > .fc-display-line, [data-fc-stagger] [data-fc-stagger-child]';

  function fcRevealFallback() {
    document.documentElement.classList.add('fc-motion-fallback');
    document.querySelectorAll(FC_PREHIDDEN_SELECTORS).forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.filter = 'none';
      el.style.visibility = 'visible';
    });
  }

  if (typeof gsap === 'undefined') {
    console.warn('[FC] GSAP not loaded — animations skipped.');
    fcRevealFallback();
    return;
  }

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Animated CSS blur() forces a full re-raster of the target every
     frame — fine on desktop GPUs, but a primary source of scroll
     jank on phones (entrance cascades firing mid-flick read as the
     page "sticking" in sections). Below the conveyor breakpoint
     (1024px, same as initSpreadChoreography) entrances run as pure
     transform/opacity tweens; desktop keeps the blur-resolve. */
  const FC_BLUR_OK = window.matchMedia('(min-width: 1024px)').matches;
  const FC_EASE = 'expo.out';
  const FC_BLUR_TEXT = 8;
  const FC_BLUR_IMAGE = 12;
  const FC_BLUR_PRODUCT = 10;

  document.documentElement.classList.add('fc-js');
  if (REDUCED) document.documentElement.classList.add('fc-motion-reduced');

  if (typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
    /* Mobile browsers fire resize when the URL bar collapses/expands
       during scroll; a full refresh mid-scroll makes pinned and
       triggered layouts jump. Width/orientation changes still refresh. */
    ScrollTrigger.config({ ignoreMobileResize: true });
  } else {
    console.warn('[FC] ScrollTrigger not loaded — scroll-driven motion is disabled.');
    /* Scroll-revealed elements are pre-hidden in CSS; without
       ScrollTrigger no trigger will ever fire, so show them now. */
    fcRevealFallback();
  }

  /** Clear GPU blur after a tween so text stays crisp. */
  function fcClearFilter(el) {
    if (el) gsap.set(el, { filter: 'none' });
  }

  /** Blur start value for an entrance — 'none' on mobile (no animated filter). */
  function fcBlurFrom(px) {
    if (!FC_BLUR_OK) return 'none';
    return 'blur(' + (px != null ? px : FC_BLUR_TEXT) + 'px)';
  }

  /** Blur end value for an entrance — matches fcBlurFrom's format. */
  function fcBlurTo() {
    return FC_BLUR_OK ? 'blur(0px)' : 'none';
  }

  /** Editorial text entrance — subtle lift + defocus resolve. */
  function fcTextHidden(y) {
    var out = { autoAlpha: 0, y: y != null ? y : 24 };
    if (FC_BLUR_OK) out.filter = 'blur(' + FC_BLUR_TEXT + 'px)';
    return out;
  }

  function fcTextVisible(extra) {
    var out = { autoAlpha: 1, y: 0 };
    if (FC_BLUR_OK) out.filter = 'blur(0px)';
    if (extra) Object.keys(extra).forEach(function (k) { out[k] = extra[k]; });
    return out;
  }

  /** Product / photo entrance — deeper blur + gentle scale settle. */
  function fcImageHidden(y, blur, scale) {
    var out = {
      autoAlpha: 0,
      y: y != null ? y : 36,
      scale: scale != null ? scale : 0.98,
    };
    if (FC_BLUR_OK) out.filter = 'blur(' + (blur != null ? blur : FC_BLUR_IMAGE) + 'px)';
    return out;
  }

  function fcImageVisible(extra) {
    var out = { autoAlpha: 1, y: 0, scale: 1 };
    if (FC_BLUR_OK) out.filter = 'blur(0px)';
    if (extra) Object.keys(extra).forEach(function (k) { out[k] = extra[k]; });
    return out;
  }

  function fcRevealComplete(el) {
    return function () {
      fcClearFilter(el);
      if (el && el.style) el.style.willChange = 'auto';
    };
  }

  /** If keyboard focus reaches hero before GSAP entrance finishes, show copy immediately. */
  function initHeroFocusReveal(root) {
    var scope = root && root.querySelector ? root : document;
    var hero =
      scope.querySelector && scope.querySelector('.fc-hero--photo-dominant')
        ? scope.querySelector('.fc-hero--photo-dominant')
        : scope.classList && scope.classList.contains('fc-hero--photo-dominant')
          ? scope
          : null;
    if (!hero || REDUCED || typeof gsap === 'undefined') return;
    if (hero.getAttribute('data-fc-hero-focus-init') === 'true') return;

    function revealHeroCopy() {
      var items = hero.querySelectorAll('.fc-hero-anim');
      items.forEach(function (el) {
        gsap.set(el, { autoAlpha: 1, y: 0, filter: 'none', clearProps: 'transform,filter' });
        if (el.style) el.style.willChange = 'auto';
      });
    }

    hero.addEventListener('focusin', revealHeroCopy, { once: true });
    hero.setAttribute('data-fc-hero-focus-init', 'true');
  }

  /** Scroll-triggered blur-fade-up (requires ScrollTrigger). */
  function fcScrollReveal(elements, options) {
    if (REDUCED || typeof ScrollTrigger === 'undefined') return;
    options = options || {};
    var start = options.start || 'top 88%';
    var y = options.y != null ? options.y : 28;
    var duration = options.duration || 0.9;
    var ease = options.ease || FC_EASE;
    var trigger = options.trigger;
    var blur = options.blur != null ? options.blur : FC_BLUR_TEXT;
    var scale = options.scale;

    gsap.utils.toArray(elements).forEach(function (el) {
      var from = { autoAlpha: 0, y: y };
      var to = { autoAlpha: 1, y: 0 };
      if (FC_BLUR_OK) {
        from.filter = 'blur(' + blur + 'px)';
        to.filter = 'blur(0px)';
      }
      if (scale != null) {
        from.scale = scale;
        to.scale = 1;
      }
      gsap.fromTo(
        el,
        from,
        Object.assign({}, to, {
          duration: duration,
          ease: ease,
          onComplete: fcRevealComplete(el),
          scrollTrigger: {
            trigger: trigger || el,
            start: start,
            once: true,
            invalidateOnRefresh: true,
          },
        })
      );
    });
  }

  function refreshScrollMotion() {
    if (typeof ScrollTrigger === 'undefined') return;
    ScrollTrigger.refresh();
  }

  /**
   * Lazy-loaded images (spread PNGs, split photos) finish after
   * window.load and change layout heights, leaving trigger positions
   * stale — reveals then fire at the wrong scroll offsets. One
   * debounced refresh after each burst of image loads keeps every
   * trigger's start/end in sync with the settled layout.
   */
  function initImageLoadRefresh() {
    if (typeof ScrollTrigger === 'undefined') return;
    var timer = null;

    /* Lazy images load in bursts WHILE the user is scrolling; a full
       ScrollTrigger.refresh() forces layout of every trigger and, run
       mid-flick, reads as the page hitching. Hold the refresh until
       the scroll has settled — trigger positions only matter for
       sections the user hasn't reached yet. */
    function refreshWhenIdle() {
      timer = null;
      if (typeof ScrollTrigger.isScrolling === 'function' && ScrollTrigger.isScrolling()) {
        timer = setTimeout(refreshWhenIdle, 250);
        return;
      }
      ScrollTrigger.refresh();
    }

    document.addEventListener(
      'load',
      function (event) {
        var el = event.target;
        if (!el || el.tagName !== 'IMG') return;
        if (timer) clearTimeout(timer);
        timer = setTimeout(refreshWhenIdle, 200);
      },
      true
    );
  }

  function isDesignMode() {
    return (
      document.documentElement.classList.contains('shopify-design-mode') ||
      (typeof window.Shopify !== 'undefined' && window.Shopify.designMode)
    );
  }

  function resolveHeroSection(event) {
    var target = event && event.target;
    if (target instanceof HTMLElement && sectionHasHeroAnim(target)) {
      return target;
    }
    var sectionId = event && event.detail && event.detail.sectionId;
    if (!sectionId) return null;
    var byId = document.getElementById('shopify-section-' + sectionId);
    if (byId instanceof HTMLElement && sectionHasHeroAnim(byId)) {
      return byId;
    }
    return null;
  }

  function handleHeroSectionEvent(event) {
    var section = resolveHeroSection(event);
    if (!section) return;
    reinitHeroInSection(section);
  }

  function isCollectionHeroSection(section) {
    if (!section) return false;
    return (
      section.matches('[data-section-type="fc-collection-hero"]') ||
      section.id === 'fc-collection-hero' ||
      !!section.querySelector('[data-section-type="fc-collection-hero"], #fc-collection-hero')
    );
  }

  function revealCollectionHeroInstant(scope) {
    var copyItems = scope.querySelectorAll('.fc-hero-anim');
    var product = scope.querySelector('.fc-collection-hero__product-inner');
    var breathe = scope.querySelector('.fc-hero__breathe-wrap');
    if (copyItems.length) gsap.set(copyItems, { autoAlpha: 1, y: 0, clearProps: 'transform,opacity,visibility' });
    if (product) gsap.set(product, { autoAlpha: 1, y: 0, scale: 1, filter: 'none', clearProps: 'all' });
    if (breathe) gsap.set(breathe, { scale: 1, clearProps: 'transform' });
  }

  /** Collection landing hero — editorial timeline (y only, no horizontal motion). */
  function initCollectionHeroChoreography(root) {
    var scope = root || document;
    var hero =
      scope.matches && scope.matches('[data-section-type="fc-collection-hero"], #fc-collection-hero')
        ? scope
        : scope.querySelector('[data-section-type="fc-collection-hero"], #fc-collection-hero');
    if (!hero) return;

    var copyItems = hero.querySelectorAll('.fc-hero-anim');
    var product = hero.querySelector('.fc-collection-hero__product-inner');
    var breathe = hero.querySelector('.fc-hero__breathe-wrap');
    var targets = [];
    if (copyItems.length) targets = targets.concat(Array.prototype.slice.call(copyItems));
    if (product) targets.push(product);
    if (breathe) targets.push(breathe);

    gsap.killTweensOf(targets);

    if (REDUCED || isDesignMode()) {
      revealCollectionHeroInstant(hero);
      return;
    }

    var tl = gsap.timeline({ defaults: { ease: FC_EASE } });

    if (breathe) {
      gsap.set(breathe, { scale: 1.04, transformOrigin: 'center center' });
      tl.to(breathe, { scale: 1, duration: 1.15 }, 0);
      tl.add(function () {
        gsap.to(breathe, {
          scale: 1.02,
          duration: 14,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
        });
      });
    }

    if (copyItems.length) {
      gsap.set(copyItems, fcTextHidden(24));
      tl.to(
        copyItems,
        Object.assign({}, fcTextVisible(), {
          duration: 0.95,
          stagger: 0.1,
          onComplete: function () {
            copyItems.forEach(fcClearFilter);
          },
        }),
        breathe ? 0.12 : 0.08
      );
    }

    if (product) {
      gsap.set(product, fcImageHidden(36, FC_BLUR_PRODUCT, 0.96));
      tl.to(
        product,
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          filter: fcBlurTo(),
          duration: 1.1,
          onComplete: function () {
            gsap.set(product, { clearProps: 'filter' });
          },
        },
        0.32
      );
    }

    tl.eventCallback('onComplete', refreshScrollMotion);
  }

  function initHeroEntrance(root) {
    var scope = root || document;
    var items = scope.querySelectorAll('.fc-hero-anim');
    if (!items.length) return;

    gsap.killTweensOf(items);

    if (REDUCED || isDesignMode()) {
      gsap.set(items, { autoAlpha: 1, y: 0, clearProps: 'transform,opacity,visibility' });
      return;
    }

    gsap.fromTo(
      items,
      fcTextHidden(22),
      Object.assign({}, fcTextVisible(), {
        duration: 0.9,
        ease: FC_EASE,
        stagger: 0.08,
        delay: 0.08,
        onComplete: function () {
          items.forEach(fcClearFilter);
        },
      })
    );
  }

  function initHeroPhotoBreathe(root) {
    var scope = root || document;
    var breathe = scope.querySelector('.fc-hero__breathe-wrap');
    if (!breathe || REDUCED) return;
    /* Scale the inner image directly so the wrap stays at 100% size and
       can keep clipping. Falls back to any direct <img> child if no class
       matches (Shopify image_tag can drop the class attribute). */
    var photo =
      breathe.querySelector('.fc-hero__fullbleed-img, .fc-hero__fullbleed-placeholder') ||
      breathe.querySelector(':scope > img');
    var motionTarget = photo || breathe;

    gsap.killTweensOf(motionTarget);

    if (isDesignMode()) {
      gsap.set(motionTarget, { scale: 1.04, transformOrigin: 'center center' });
      return;
    }

    gsap.set(motionTarget, { transformOrigin: 'center center' });
    gsap.fromTo(
      motionTarget,
      { scale: 1.08 },
      {
        scale: 1.04,
        duration: 1.2,
        ease: FC_EASE,
        delay: 0.08,
        onComplete: function () {
          gsap.to(motionTarget, {
            scale: 1.06,
            duration: 12,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
  }

  /** Legacy overlay menu — no-op if markup removed */
  function initMobileMenu() {
    var menu = document.querySelector('.fc-mobile-menu');
    var openBtn = document.querySelector('.fc-mobile-menu-open');
    var closeBtn = menu && menu.querySelector('.fc-mobile-menu__close');
    if (!menu) return;

    var links = menu.querySelectorAll('.fc-mobile-nav__link, .fc-mobile-menu__cta');

    function openMenu() {
      document.body.style.overflow = 'hidden';
      gsap.set(menu, { display: 'flex' });
      if (REDUCED) {
        gsap.set(menu, { autoAlpha: 1 });
        return;
      }
      gsap.fromTo(menu, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.35, ease: 'power2.out' });
      if (links.length) {
        gsap.fromTo(
          links,
          { opacity: 0, y: 18 },
          { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.07, delay: 0.1 }
        );
      }
    }

    function closeMenu() {
      if (REDUCED) {
        gsap.set(menu, { autoAlpha: 0, display: 'none' });
        document.body.style.overflow = '';
        return;
      }
      gsap.to(menu, {
        autoAlpha: 0,
        duration: 0.25,
        ease: 'power2.in',
        onComplete: function () {
          gsap.set(menu, { display: 'none' });
          document.body.style.overflow = '';
        },
      });
    }

    if (openBtn) openBtn.addEventListener('click', openMenu);
    if (closeBtn) closeBtn.addEventListener('click', closeMenu);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.style.display === 'flex') closeMenu();
    });
  }

  function initScrollReveals() {
    var revealEls = document.querySelectorAll('.fc-reveal');
    if (!revealEls.length) return;
    revealEls = Array.prototype.filter.call(revealEls, function (el) {
      return !el.closest('[data-fc-home-locator], [data-fc-home-retailers], [data-fc-extracts-stats]');
    });
    if (!revealEls.length) return;

    /* Photo / media wrappers resolve with the image recipe (deeper
       blur + gentle scale settle); everything else uses the text lift. */
    var mediaEls = revealEls.filter(function (el) {
      return el.classList.contains('fc-reveal--media');
    });
    var textEls = revealEls.filter(function (el) {
      return !el.classList.contains('fc-reveal--media');
    });

    if (textEls.length) fcScrollReveal(textEls, { y: 28, duration: 0.85 });
    if (mediaEls.length) {
      fcScrollReveal(mediaEls, { y: 32, duration: 1.05, blur: FC_BLUR_IMAGE, scale: 0.98 });
    }
  }

  /** Collection spread product PNG — plays only when section enters view. */
  function playSpreadProductEntrance(t, options) {
    options = options || {};
    if (!t || !t.mediaReveal) return;

    var targets = [t.mediaReveal];
    if (t.mediaFrame) targets.push(t.mediaFrame);
    gsap.killTweensOf(targets);

    gsap.fromTo(
      t.mediaReveal,
      {
        autoAlpha: 0,
        y: options.y != null ? options.y : 28,
        scale: 0.98,
        filter: fcBlurFrom(options.blur != null ? options.blur : FC_BLUR_PRODUCT),
      },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        filter: fcBlurTo(),
        duration: options.duration || 0.95,
        ease: FC_EASE,
        onComplete: fcRevealComplete(t.mediaReveal),
      }
    );

    if (t.mediaFrame) {
      gsap.fromTo(
        t.mediaFrame,
        { scale: 1.03, transformOrigin: 'center center' },
        { scale: 1, duration: options.duration || 0.95, ease: FC_EASE }
      );
    }
  }

  /** Bind product entrance to scroll — never pre-hide/blur before the trigger. */
  function bindSpreadProductInView(section, t, opts) {
    if (REDUCED || !t || !t.mediaReveal) return;
    opts = opts || {};

    ScrollTrigger.create({
      trigger: opts.trigger || section,
      start: opts.start || 'top 85%',
      once: true,
      invalidateOnRefresh: true,
      onEnter: function () {
        /* A post-load ScrollTrigger.refresh() can fire this once-trigger
           while the section is already scrolled past (e.g. reload
           mid-page). Animating there would flash off-screen — compose
           instantly instead. */
        var rect = (opts.trigger || section).getBoundingClientRect();
        if (rect.bottom <= 0) {
          var targets = [t.mediaReveal];
          if (t.mediaFrame) targets.push(t.mediaFrame);
          gsap.set(targets, {
            autoAlpha: 1, y: 0, scale: 1, filter: 'none', clearProps: 'filter,transform',
          });
          return;
        }
        playSpreadProductEntrance(t, opts);
      },
    });
  }

  /** Magazine collection spreads + retailers row (homepage-blocks.jsx) */
  /**
   * Spreads — locked-stage vertical conveyor.
   * Full spec: docs/spread-conveyor-spec.md.
   *
   * Architecture (desktop ≥1024px, motion allowed): .fc-spread-stack is
   * pinned at 100svh and every spread section sits stationary at
   * inset: 0 (see fc-homepage.css). Content behaves like a vertical
   * conveyor with a hold at center:
   *
   *   • Each section's BACKGROUND lives on a dedicated child layer
   *     ([data-fc-spread-bg]) decoupled from the content, so the
   *     environment crossfades as its own beat while the hero
   *     ([data-fc-spread-hero]) is driven purely by translation.
   *   • Enter: hero rises +18vh → 0 (expo.out) as copy pops line-by-
   *     line and images fade up a beat behind. The incoming bg
   *     finishes fading by ~55% of the window — the environment is
   *     established before the composition locks into it.
   *   • Exit: hero translates 0 → -14vh (power2.in) and softens while
   *     the next collection is already rising from below.
   *   • Outgoing backgrounds never fade out — they stay opaque
   *     beneath the incoming one, so the stage can never be
   *     see-through and reverse scroll is automatically correct.
   *   • ScrollTrigger snap pulls the playhead out of any transition
   *     window to a rest point (directional) — the "lock".
   *
   * Every collection, INCLUDING the first, enters via the conveyor
   * (a lead-in window right after the pin engages). There is no
   * separate one-shot entrance on desktop — that fragile trigger was
   * the source of the "no entrance / already loaded" bug.
   *
   *   • Desktop (≥ 1024px, motion allowed):  pinned conveyor stage
   *   • Mobile (<1024px) or reduced motion:  one-shot on-enter cascade,
   *     natural document flow
   *
   * gsap.matchMedia() handles both contexts and cleans up the
   * inactive one when the viewport crosses 1024px or motion prefs
   * change.
   */
  function initSpreadChoreography() {
    if (typeof ScrollTrigger === 'undefined') return;

    var stack = document.querySelector('.fc-spread-stack');
    var sections = Array.from(
      document.querySelectorAll('[data-fc-spread-section][data-fc-spread-slug]')
    );
    if (!sections.length || !stack) return;

    var numSpreads = sections.length;

    if (isDesignMode()) {
      /* Theme editor re-renders section HTML at will; a pin + scrub
         bound to stale nodes leaves the preview blank or mid-animation.
         Show every spread fully composed instead. */
      sections.forEach(function (section) {
        setRevealed(collectTargets(section));
      });
      return;
    }

    /* ── Target collection helpers ─────────────────────────── */
    function collectTargets(section) {
      return {
        topline:      section.querySelector('[data-fc-spread-topline]'),
        wordmark:     section.querySelector('.fc-spread__wordmark--in-text'),
        media:        section.querySelector('[data-fc-spread-media]'),
        mediaFrame:   section.querySelector('[data-fc-spread-media-frame]'),
        mediaReveal:  section.querySelector('[data-fc-spread-media-reveal]'),
        headlineLines: section.querySelectorAll('[data-fc-spread-headline-line]'),
        eyebrow:      section.querySelector('.fc-spread__eyebrow'),
        eyebrowRule:  section.querySelector('[data-fc-eyebrow-rule]'),
        body:         section.querySelector('.fc-spread__body'),
        cta:          section.querySelector('.fc-spread-cta'),
      };
    }

    function setProductRevealed(t) {
      if (t.media) gsap.set(t.media, { autoAlpha: 1 });
      if (t.mediaFrame) gsap.set(t.mediaFrame, { scale: 1, clearProps: 'transform' });
      if (t.mediaReveal) {
        gsap.set(t.mediaReveal, { autoAlpha: 1, y: 0, scale: 1, filter: 'none', clearProps: 'filter,transform' });
      }
    }

    function setRevealed(t) {
      if (t.topline)      gsap.set(t.topline,      { autoAlpha: 1, y: 0, filter: 'none' });
      if (t.wordmark)     gsap.set(t.wordmark,     { autoAlpha: 1, y: 0, filter: 'none' });
      setProductRevealed(t);
      if (t.eyebrowRule)  gsap.set(t.eyebrowRule,  { scaleX: 1 });
      if (t.headlineLines.length) {
        gsap.set(t.headlineLines, { yPercent: 0, autoAlpha: 1, filter: 'none' });
      }
      if (t.body)         gsap.set(t.body,         { autoAlpha: 1, y: 0, filter: 'none' });
      if (t.cta)          gsap.set(t.cta,          { autoAlpha: 1, y: 0, filter: 'none' });
    }

    /** Card 1 product — scroll-into-view only (no init-time hide/blur). */
    function bindSpreadCopyInView(section, t, opts) {
      if (REDUCED) return;
      opts = opts || {};
      var copyTargets = [];
      if (t.topline) copyTargets.push(t.topline);
      if (t.wordmark) copyTargets.push(t.wordmark);
      if (t.body) copyTargets.push(t.body);
      if (t.cta) copyTargets.push(t.cta);
      if (t.headlineLines.length) {
        copyTargets = copyTargets.concat(Array.prototype.slice.call(t.headlineLines));
      }
      if (!copyTargets.length && !t.eyebrowRule) return;

      ScrollTrigger.create({
        trigger: opts.trigger || section,
        start: opts.start || 'top 85%',
        once: true,
        invalidateOnRefresh: true,
        onEnter: function () {
          /* Same off-screen guard as the product binder: if a refresh
             fires this once-trigger after the section was scrolled
             past, snap the copy composed instead of animating unseen. */
          var rect = (opts.trigger || section).getBoundingClientRect();
          if (rect.bottom <= 0) {
            setRevealed(t);
            return;
          }
          var tl = gsap.timeline({ defaults: { ease: FC_EASE } });
          if (t.topline) {
            tl.fromTo(t.topline, fcTextHidden(14), Object.assign({}, fcTextVisible(), { duration: 0.7, onComplete: fcRevealComplete(t.topline) }), 0.08);
          }
          if (t.wordmark) {
            tl.fromTo(t.wordmark, fcTextHidden(16), Object.assign({}, fcTextVisible(), { duration: 0.75, onComplete: fcRevealComplete(t.wordmark) }), 0.14);
          }
          if (t.eyebrowRule) {
            gsap.set(t.eyebrowRule, { scaleX: 0, transformOrigin: 'left center' });
            tl.to(t.eyebrowRule, { scaleX: 1, duration: 0.65 }, 0.22);
          }
          if (t.headlineLines.length) {
            gsap.set(t.headlineLines, { yPercent: 110, autoAlpha: 0, filter: fcBlurFrom(FC_BLUR_TEXT) });
            tl.to(t.headlineLines, {
              yPercent: 0, autoAlpha: 1, filter: fcBlurTo(), duration: 0.85, stagger: 0.08,
              onComplete: function () { t.headlineLines.forEach(fcClearFilter); },
            }, 0.28);
          }
          if (t.body) {
            tl.fromTo(t.body, fcTextHidden(18), Object.assign({}, fcTextVisible(), { duration: 0.75, onComplete: fcRevealComplete(t.body) }), 0.5);
          }
          if (t.cta) {
            tl.fromTo(t.cta, fcTextHidden(14), Object.assign({}, fcTextVisible(), { duration: 0.75, onComplete: fcRevealComplete(t.cta) }), 0.62);
          }
        },
      });
    }

    var mm = gsap.matchMedia();

    mm.add(
      {
        isDesktop: '(min-width: 1024px) and (prefers-reduced-motion: no-preference)',
        isCompact: '(max-width: 1023px), (prefers-reduced-motion: reduce)',
      },
      function (ctx) {
        var conds = ctx.conditions;

        /* ════════════════════════════════════════════════════
           DESKTOP — Locked-stage vertical conveyor
           (docs/spread-conveyor-spec.md — the locked values)
           ════════════════════════════════════════════════════
           ENTER₀ → DWELL → SWAP₀→₁ → DWELL → SWAP₁→₂ → DWELL
           Waiting  (below): hero +30vh, copy/images hidden, bg 0
           Resting (locked): everything composed, bg 1
           Exited   (above): hero -22vh, content softened; bg
                             STAYS opaque beneath the next layer  */

        if (conds.isDesktop) {
          /* 1 timeline unit ≈ 1 svh of scroll (see end, below). */
          var ENTER0 = 1.0;
          var DWELL = 0.5;
          var SWAP = 1.5;
          var totalUnits = ENTER0 + numSpreads * DWELL + (numSpreads - 1) * SWAP;

          var heroes = sections.map(function (section) {
            return section.querySelector('[data-fc-spread-hero]');
          });
          var textCols = sections.map(function (section) {
            return section.querySelector('[data-fc-spread-text]');
          });
          var bgs = sections.map(function (section) {
            return section.querySelector('[data-fc-spread-bg]');
          });
          var targets = sections.map(collectTargets);

          function vhPx(percent) {
            return window.innerHeight * (percent / 100);
          }

          /* Waiting state — every collection (card 1 included) starts
             below the stage and enters via the conveyor. JS-only hiding,
             never CSS: a script failure shows everything composed. */
          function setWaiting(i) {
            var t = targets[i];
            if (bgs[i])    gsap.set(bgs[i], { autoAlpha: 0 });
            if (heroes[i]) gsap.set(heroes[i], { y: vhPx(30), force3D: true });
            if (t.topline) gsap.set(t.topline, fcTextHidden(24));
            if (t.wordmark) gsap.set(t.wordmark, fcTextHidden(24));
            if (t.eyebrow) gsap.set(t.eyebrow, fcTextHidden(18));
            if (t.eyebrowRule) gsap.set(t.eyebrowRule, { scaleX: 0, transformOrigin: 'left center' });
            if (t.headlineLines.length) {
              gsap.set(t.headlineLines, {
                yPercent: 110, autoAlpha: 0, filter: 'blur(' + FC_BLUR_TEXT + 'px)',
              });
            }
            if (t.body) gsap.set(t.body, fcTextHidden(24));
            if (t.cta)  gsap.set(t.cta, fcTextHidden(24));
            /* Hide the whole media column (it carries a bg color and the
               edge-fade overlay, which would float over lower layers);
               the reveal wrapper carries the travel/blur. */
            if (t.media) gsap.set(t.media, { autoAlpha: 0 });
            if (t.mediaReveal) {
              gsap.set(t.mediaReveal, { y: vhPx(12), scale: 0.96, filter: 'blur(14px)' });
            }
          }

          sections.forEach(function (section, i) { setWaiting(i); });

          /* Arrival: Waiting → Resting inside [at, at + dur].
             Environment first (bg done by 55% — the set is ready before
             the actor arrives), then text pops line-by-line, images fade
             up ~0.07·dur behind the text and span most of the window
             (long, slow product arrival). All eases decelerate (expo.out).
             Blur stays inside the scrubbed tweens — never cleared — the
             scrub must be able to reverse through it. */
          function addArrival(tl, i, at, dur) {
            var t = targets[i];
            if (bgs[i]) {
              tl.to(bgs[i], { autoAlpha: 1, duration: dur * 0.55, ease: 'none' }, at);
            }
            if (heroes[i]) {
              tl.to(heroes[i], { y: 0, duration: dur, ease: FC_EASE }, at);
            }
            if (t.topline) {
              tl.to(t.topline, fcTextVisible({ duration: dur * 0.35, ease: FC_EASE }), at + dur * 0.05);
            }
            if (t.wordmark) {
              tl.to(t.wordmark, fcTextVisible({ duration: dur * 0.35, ease: FC_EASE }), at + dur * 0.05);
            }
            if (t.eyebrow) {
              tl.to(t.eyebrow, fcTextVisible({ duration: dur * 0.3, ease: FC_EASE }), at + dur * 0.1);
            }
            if (t.eyebrowRule) {
              tl.to(t.eyebrowRule, { scaleX: 1, duration: dur * 0.3, ease: FC_EASE }, at + dur * 0.12);
            }
            if (t.headlineLines.length) {
              tl.to(t.headlineLines, {
                yPercent: 0, autoAlpha: 1, filter: 'blur(0px)',
                duration: dur * 0.5, stagger: dur * 0.06, ease: FC_EASE,
              }, at + dur * 0.15);
            }
            if (t.body) {
              tl.to(t.body, fcTextVisible({ duration: dur * 0.4, ease: FC_EASE }), at + dur * 0.35);
            }
            if (t.cta) {
              tl.to(t.cta, fcTextVisible({ duration: dur * 0.4, ease: FC_EASE }), at + dur * 0.45);
            }
            if (t.media) {
              tl.to(t.media, { autoAlpha: 1, duration: dur * 0.45, ease: 'none' }, at + dur * 0.2);
            }
            if (t.mediaReveal) {
              tl.to(t.mediaReveal, {
                y: 0, scale: 1, filter: 'blur(0px)', duration: dur * 0.65, ease: FC_EASE,
              }, at + dur * 0.22);
            }
          }

          /* Departure: Resting → Exited inside [at, at + dur]. The
             carriage accelerates up and out (power2.in) and softens;
             the incoming layer's bg curtains it by ~55% of the window.
             Its own bg intentionally stays opaque underneath. */
          function addDeparture(tl, i, at, dur) {
            var t = targets[i];
            if (heroes[i]) {
              tl.to(heroes[i], {
                y: function () { return -vhPx(22); },
                duration: dur, ease: 'power2.in',
              }, at);
            }
            if (textCols[i]) {
              tl.to(textCols[i], {
                autoAlpha: 0.2, filter: 'blur(6px)', duration: dur, ease: 'power2.in',
              }, at);
            }
            if (t.topline) {
              tl.to(t.topline, { autoAlpha: 0.2, duration: dur, ease: 'power2.in' }, at);
            }
            if (t.media) {
              tl.to(t.media, { autoAlpha: 0.15, duration: dur, ease: 'power2.in' }, at);
            }
            if (t.mediaReveal) {
              tl.to(t.mediaReveal, {
                y: function () { return -vhPx(8); },
                filter: 'blur(8px)', duration: dur, ease: 'power2.in',
              }, at);
            }
          }

          /* Snap = the lock. Mid-dwell rests are left alone; rests inside
             the lead-in or a swap window glide to the boundary in the
             direction of travel, so the user always lands on a composed
             spread. */
          function snapToRest(progress, self) {
            var unit = progress * totalUnits;
            var direction = self && self.direction ? self.direction : 0;
            if (unit > 0 && unit < ENTER0) {
              var lead = direction < 0 ? 0
                : direction > 0 ? ENTER0
                : unit < ENTER0 / 2 ? 0 : ENTER0;
              return lead / totalUnits;
            }
            for (var i = 0; i < numSpreads - 1; i++) {
              var swapStart = ENTER0 + (i + 1) * DWELL + i * SWAP;
              var swapEnd = swapStart + SWAP;
              if (unit > swapStart && unit < swapEnd) {
                var target;
                if (direction > 0) target = swapEnd;
                else if (direction < 0) target = swapStart;
                else target = unit - swapStart < SWAP / 2 ? swapStart : swapEnd;
                return target / totalUnits;
              }
            }
            return progress;
          }

          var mainTL = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              trigger:    stack,
              start:      'top top',
              // Function-based so every refresh (resize, orientation
              // change) re-syncs the pin distance to the current viewport
              // instead of keeping the height captured at init.
              end:        function () {
                return '+=' + Math.round(totalUnits * window.innerHeight);
              },
              pin:        true,
              pinSpacing: true,
              anticipatePin: 1,
              scrub:      0.55,
              fastScrollEnd: true,
              invalidateOnRefresh: true,
              snap: {
                snapTo: snapToRest,
                duration: { min: 0.3, max: 0.7 },
                ease: 'power2.out',
                delay: 0.1,
              },
            },
          });

          // Lead-in: collection 1 rises and locks the moment the pin
          // engages — the "drop and hold" on entering the spreads.
          addArrival(mainTL, 0, 0, ENTER0);

          // Conveyor swaps: outgoing rises out while incoming rises in,
          // sharing the same window so the compositions cross and the
          // stage is never empty.
          sections.forEach(function (section, i) {
            if (i === 0) return;
            var at = ENTER0 + i * DWELL + (i - 1) * SWAP;
            addDeparture(mainTL, i - 1, at, SWAP);
            addArrival(mainTL, i, at, SWAP);
          });

          // Trailing dwell: the last spread rests fully composed before
          // the pin releases into the next band.
          mainTL.to({}, { duration: DWELL }, totalUnits - DWELL);

        /* ════════════════════════════════════════════════════
           MOBILE / REDUCED MOTION — simple entrance cascades,
           no pin. Cards flow in natural document order with
           min-height:100svh. Reduced motion skips all motion.
           ════════════════════════════════════════════════════ */
        } else {
          sections.forEach(function (section) {
            var t = collectTargets(section);

            if (REDUCED) {
              setRevealed(t);
              return;
            }

            bindSpreadProductInView(section, t, { start: 'top 85%' });
            bindSpreadCopyInView(section, t, { start: 'top 85%' });
          });
        }
      }
    );
    /* No local load listener — init() owns the single window.load refresh. */
  }

  function fcHeadlineLines(headline) {
    if (!headline) return [];
    return headline.querySelectorAll(
      ':scope > span, :scope > em, :scope > .fc-display-line, :scope > .fc-collection-hero__headline-line, :scope > .fc-collection-hero__headline-em, :scope > [class*="headline-line"], :scope > [class*="headline-em"]'
    );
  }

  /** Stagger display-stack lines inside a timeline. Returns next start time. */
  function fcAnimateHeadlineInTimeline(tl, headline, at, options) {
    options = options || {};
    var lines = fcHeadlineLines(headline);
    if (!lines.length) return at;
    gsap.set(headline, { autoAlpha: 1 });
    gsap.set(lines, fcTextHidden(options.y != null ? options.y : 22));
    tl.to(
      lines,
      Object.assign({}, fcTextVisible(), {
        duration: options.duration || 0.9,
        stagger: options.stagger || 0.1,
        onComplete: function () {
          lines.forEach(fcClearFilter);
        },
      }),
      at
    );
    return at + (options.tail != null ? options.tail : 0.28);
  }

  function fcResetMotionTargets(elements) {
    gsap.utils.toArray(elements).forEach(function (el) {
      gsap.set(el, { autoAlpha: 1, y: 0, x: 0, scale: 1, filter: 'none', clearProps: 'transform' });
    });
  }

  /** Wine / editorial split — photo resolves, then copy cascades in the text column. */
  function animateSplitTextColumn(root, textCol) {
    if (!textCol) return;

    var headline = textCol.querySelector('[data-fc-headline-reveal]');
    var copyItems = textCol.querySelectorAll('[data-fc-split-copy]');
    var hasOrchestration = headline || copyItems.length;

    if (REDUCED) {
      if (headline) gsap.set(headline, { autoAlpha: 1 });
      fcResetMotionTargets(fcHeadlineLines(headline));
      fcResetMotionTargets(copyItems);
      gsap.set(textCol, { autoAlpha: 1, y: 0 });
      return;
    }

    if (!hasOrchestration) {
      gsap.fromTo(
        textCol,
        fcTextHidden(24),
        Object.assign({}, fcTextVisible(), {
          duration: 0.9,
          ease: FC_EASE,
          delay: 0.1,
          onComplete: fcRevealComplete(textCol),
          scrollTrigger: {
            trigger: root,
            start: 'top 80%',
            once: true,
            invalidateOnRefresh: true,
          },
        })
      );
      return;
    }

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top 80%',
        once: true,
        invalidateOnRefresh: true,
      },
    });

    var t = 0.1;
    if (headline) {
      t = fcAnimateHeadlineInTimeline(tl, headline, t, { y: 20, tail: 0.32 });
    }
    if (copyItems.length) {
      gsap.set(copyItems, fcTextHidden(18));
      tl.to(
        copyItems,
        Object.assign({}, fcTextVisible(), {
          duration: 0.85,
          stagger: 0.08,
          onComplete: function () {
            copyItems.forEach(fcClearFilter);
          },
        }),
        t
      );
    }
  }

  /** Wine split — photo scale + orchestrated text column */
  function initSplitReveal() {
    if (typeof ScrollTrigger === 'undefined') return;
    /* Was querySelector — only animated the first section. Use
       querySelectorAll so multiple split-reveals on the same page
       all fire correctly. */
    var roots = document.querySelectorAll('[data-fc-split-reveal]');
    if (!roots.length) return;

    Array.prototype.forEach.call(roots, function (root) {
      /* Wine trio uses [data-fc-split-photo] per figure; legacy splits
         still carry a single .fc-split-block__photo. Support both. */
      var photos = root.querySelectorAll('[data-fc-split-photo]');
      if (!photos.length) photos = root.querySelectorAll('.fc-split-block__photo');
      var textCol = root.querySelector('.fc-split-block__text');

      if (REDUCED) {
        Array.prototype.forEach.call(photos, function (ph) {
          gsap.set(ph, { autoAlpha: 1, scale: 1 });
        });
        animateSplitTextColumn(root, textCol);
        return;
      }

      if (photos.length) {
        var photoTargets = Array.prototype.map.call(photos, function (ph) {
          return ph.querySelector('img') || ph;
        });
        gsap.fromTo(
          photoTargets,
          fcImageHidden(0, FC_BLUR_IMAGE, 1.04),
          Object.assign({}, fcImageVisible(), {
            duration: 1.05,
            ease: FC_EASE,
            stagger: 0.14,
            onComplete: function () {
              photoTargets.forEach(function (t) {
                fcClearFilter(t);
                if (t && t.style) t.style.willChange = 'auto';
              });
            },
            scrollTrigger: {
              trigger: root,
              start: 'top 80%',
              once: true,
              invalidateOnRefresh: true,
            },
          })
        );
      }
      animateSplitTextColumn(root, textCol);
    });
  }

  /** Mission block — text + Michigan graphic */
  function initMissionReveal() {
    if (typeof ScrollTrigger === 'undefined') return;
    var root = document.querySelector('[data-fc-mission-reveal]');
    if (!root) return;

    var textCol = root.querySelector('.fc-home-mission__text');
    var gfx = root.querySelector('.fc-home-mission__graphic');

    if (REDUCED) {
      if (textCol) gsap.set(textCol, { autoAlpha: 1, y: 0 });
      if (gfx) gsap.set(gfx, { autoAlpha: 1, y: 0 });
      return;
    }

    if (textCol) {
      gsap.fromTo(
        textCol,
        fcTextHidden(24),
        Object.assign({}, fcTextVisible(), {
          duration: 0.95,
          ease: FC_EASE,
          onComplete: fcRevealComplete(textCol),
          scrollTrigger: {
            trigger: root,
            start: 'top 80%',
            once: true,
            invalidateOnRefresh: true,
          },
        })
      );
    }
    if (gfx) {
      gsap.fromTo(
        gfx,
        fcImageHidden(16, FC_BLUR_IMAGE, 0.96),
        Object.assign({}, fcImageVisible(), {
          duration: 1.05,
          ease: FC_EASE,
          delay: 0.12,
          onComplete: fcRevealComplete(gfx),
          scrollTrigger: {
            trigger: root,
            start: 'top 80%',
            once: true,
            invalidateOnRefresh: true,
          },
        })
      );
    }
  }

  /**
   * Stagger group reveals — sibling staggered fade-up.
   * Markup: <parent data-fc-stagger> with children [data-fc-stagger-child].
   * Children rise 24px and fade in 120ms apart when parent enters viewport.
   * Used by: shortcuts grid (4 beats), spreads intro, future grid reveals.
   */
  function initStaggerGroups() {
    if (typeof ScrollTrigger === 'undefined') return;
    var groups = document.querySelectorAll('[data-fc-stagger]');
    if (!groups.length) return;

    Array.prototype.forEach.call(groups, function (group) {
      if (group.hasAttribute('data-fc-stagger-init')) return;
      var children = group.querySelectorAll('[data-fc-stagger-child]');
      if (!children.length) return;

      group.setAttribute('data-fc-stagger-init', '');

      if (REDUCED) {
        gsap.set(children, { autoAlpha: 1, y: 0, filter: 'none' });
        return;
      }

      gsap.set(children, fcTextHidden(24));
      gsap.to(children, Object.assign({}, fcTextVisible(), {
        duration: 0.9,
        ease: FC_EASE,
        stagger: 0.1,
        onComplete: function () {
          children.forEach(function (child) {
            fcClearFilter(child);
            if (child && child.style) child.style.willChange = 'auto';
          });
        },
        scrollTrigger: {
          trigger: group,
          start: 'top 85%',
          once: true,
          invalidateOnRefresh: true,
        },
      }));
    });
  }

  /**
   * Section dividers — editorial pacing between blocks.
   * Markup: <div class="fc-section-divider">.
   * Motion: two rules scaleX 0→1 from the center outward (mirror animation),
   * then the FC mark fades in to opacity 0.7. Lands once, on scroll-in.
   * Reduced-motion: appears in final state instantly.
   */
  function initSectionDividers() {
    if (typeof ScrollTrigger === 'undefined') return;
    var dividers = document.querySelectorAll('.fc-section-divider');
    if (!dividers.length) return;

    Array.prototype.forEach.call(dividers, function (divider) {
      var rules = divider.querySelectorAll('.fc-section-divider__rule');
      var mark = divider.querySelector('.fc-section-divider__mark');

      if (REDUCED) {
        if (rules.length) gsap.set(rules, { scaleX: 1 });
        if (mark) gsap.set(mark, { autoAlpha: 0.7 });
        return;
      }

      if (rules.length) gsap.set(rules, { scaleX: 0 });
      if (mark) gsap.set(mark, { autoAlpha: 0 });

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: divider,
          start: 'top 92%',
          once: true,
          invalidateOnRefresh: true,
        },
      });
      if (rules.length) {
        tl.to(rules, { scaleX: 1, duration: 0.9, ease: FC_EASE });
      }
      if (mark) {
        tl.to(mark, { autoAlpha: 0.7, duration: 0.5, ease: FC_EASE }, '-=0.4');
      }
    });
  }

  /**
   * Spread eyebrow rules — hairline extending right from the
   * eyebrow label (collection intro pattern). Draws left → right.
   */
  function initSpreadEyebrowRules() {
    if (typeof ScrollTrigger === 'undefined') return;
    var rules = document.querySelectorAll('[data-fc-eyebrow-rule]');
    if (!rules.length) return;

    Array.prototype.forEach.call(rules, function (rule) {
      // Skip rules inside collection-spread sections — those are
      // animated by initSpreadChoreography as part of the cascade.
      if (rule.closest('[data-fc-spread-section][data-fc-spread-slug]')) return;

      if (REDUCED) {
        gsap.set(rule, { scaleX: 1 });
        return;
      }
      gsap.set(rule, { scaleX: 0, transformOrigin: 'left center' });
      gsap.to(rule, {
        scaleX: 1,
        duration: 0.95,
        ease: FC_EASE,
        scrollTrigger: {
          trigger: rule,
          start: 'top 88%',
          once: true,
          invalidateOnRefresh: true,
        },
      });
    });
  }

  /**
   * Subtle scroll-tied parallax on media columns of spreads.
   * Images translate Y -8% over the section's full scroll range
   * (top bottom → bottom top). Scrubbed (not snappy). Adds depth
   * without scroll-jacking.
   */
  /**
   * Media parallax — scroll-linked Y drift on spread product photos.
   * Pushed to -16% (was -8%) so the photo reads as a layer with depth
   * against the spread frame instead of a flat thumbnail inside its
   * column. Splash (the loud beat) gets a wider range so its product
   * shot drifts noticeably more than the cream spreads.
   * Homepage-only: collection why/feature media dropped this drift
   * (inside-container translation reads odd) in favor of the standard
   * one-shot blur-fade reveal.
   */
  function initMediaParallax() {
    if (REDUCED) return;
    if (typeof ScrollTrigger === 'undefined') return;
    var medias = document.querySelectorAll('[data-fc-parallax-media]');
    if (!medias.length) return;

    /* Desktop-only: scrubbed scroll-linked drift is a per-frame tax
       phones don't need — mobile gets the plain one-shot entrance and
       fully native scrolling. gsap.matchMedia reverts the tweens and
       kills their triggers if the viewport crosses the breakpoint. */
    var mm = gsap.matchMedia();
    mm.add('(min-width: 1024px)', function () {
      Array.prototype.forEach.call(medias, function (media) {
        var img = media.querySelector('img');
        if (!img) return;

        // Skip parallax on collection-spread media. Those sections are
        // handled by initSpreadChoreography — desktop runs a pinned
        // scrubbed timeline (which would fight a separate parallax
        // ScrollTrigger), and mobile runs a one-shot cascade. Retailers
        // and other [data-fc-parallax-media] elements without a slug
        // still get the parallax drift.
        var spreadSection = media.closest('[data-fc-spread-section][data-fc-spread-slug]');
        if (spreadSection) return;

        gsap.fromTo(
          img,
          { yPercent: 0 },
          {
            yPercent: -16,
            ease: 'none',
            scrollTrigger: {
              trigger: media,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1,
            },
          }
        );
      });
    });
  }

  /** Subtle stagger when the Horizon menu drawer opens */
  function initMenuDrawerMotion() {
    if (REDUCED) return;
    var details = document.querySelector('header-drawer details, .header-drawer details');
    if (!details) return;

    var items;
    details.addEventListener('toggle', function () {
      if (!details.open) return;
      items = details.querySelectorAll(
        '.menu-drawer__menu-item a, .menu-drawer summary, .menu-drawer__menu .menu-drawer__menu-item'
      );
      if (!items.length) items = details.querySelectorAll('.menu-drawer a');
      if (!items.length) return;
      gsap.fromTo(
        items,
        { opacity: 0, x: -16 },
        { opacity: 1, x: 0, duration: 0.4, stagger: 0.035, ease: 'power2.out', overwrite: 'auto' }
      );
    });
  }

  function sectionHasHeroAnim(root) {
    if (!root || !root.querySelector) return false;
    return !!root.querySelector(
      '.fc-collection-hero, [data-section-type="fc-collection-hero"], .fc-hero--photo-dominant, [data-section-type="fc-homepage-hero"], [data-section-type="fc-about-hero"]'
    );
  }

  /** Re-run hero animations when Shopify replaces section HTML in the theme editor. */
  function reinitHeroInSection(section) {
    if (!sectionHasHeroAnim(section)) return;
    if (isCollectionHeroSection(section)) {
      initCollectionHeroChoreography(section);
      return;
    }
    initHeroEntrance(section);
    initHeroPhotoBreathe(section);
    initHeroFocusReveal(section);
  }

  /**
   * Collection product cards — layered blur-fade reveal.
   * Product PNG resolves first (deeper blur + lift), then card
   * copy follows so the grid reads as a premium editorial beat.
   */
  function initCollectionProductCards() {
    if (typeof ScrollTrigger === 'undefined') return;
    var grids = document.querySelectorAll('[data-fc-cards]');
    if (!grids.length) return;

    Array.prototype.forEach.call(grids, function (grid) {
      var cards = grid.querySelectorAll('[data-fc-card]');
      if (!cards.length) return;

      if (REDUCED) {
        gsap.set(cards, { autoAlpha: 1, y: 0, scale: 1, filter: 'none' });
        cards.forEach(function (card) {
          var img = card.querySelector('.fc-collection-tf__card-img, [data-fc-card-media] img');
          var copy = card.querySelector('[data-fc-card-copy], .fc-collection-tf__card-body');
          var productTarget = img || card.querySelector('[data-fc-card-media]');
          if (productTarget) gsap.set(productTarget, { autoAlpha: 1, y: 0, scale: 1, filter: 'none' });
          if (copy) gsap.set(copy, { autoAlpha: 1, y: 0, filter: 'none' });
        });
        return;
      }

      ScrollTrigger.create({
        trigger: grid,
        start: 'top 85%',
        once: true,
        invalidateOnRefresh: true,
        onEnter: function () {
          cards.forEach(function (card, i) {
            var img = card.querySelector('.fc-collection-tf__card-img, [data-fc-card-media] img');
            var copy = card.querySelector('[data-fc-card-copy], .fc-collection-tf__card-body');
            var productTarget = img || card.querySelector('[data-fc-card-media]');
            var delay = i * 0.12;

            gsap.fromTo(
              card,
              { autoAlpha: 0, y: 32, scale: 0.99 },
              { autoAlpha: 1, y: 0, scale: 1, duration: 0.85, ease: FC_EASE, delay: delay }
            );

            if (productTarget) {
              gsap.fromTo(
                productTarget,
                fcImageHidden(24, FC_BLUR_PRODUCT, 0.98),
                Object.assign({}, fcImageVisible(), {
                  duration: 0.95,
                  ease: FC_EASE,
                  delay: delay + 0.05,
                  onComplete: fcRevealComplete(productTarget),
                })
              );
            }

            if (copy) {
              gsap.fromTo(
                copy,
                fcTextHidden(14),
                Object.assign({}, fcTextVisible(), {
                  duration: 0.8,
                  ease: FC_EASE,
                  delay: delay + 0.16,
                  onComplete: fcRevealComplete(copy),
                })
              );
            }
          });
        },
      });
    });
  }

  /**
   * Kinetic marquee — seamless infinite horizontal scroll.
   * Markup: [data-fc-marquee] > .__viewport > .__row > .__group.
   * Clones the group so a translate of exactly one group width
   * loops seamlessly. Slows (not stops) on hover. Reduced-motion:
   * left static, no clone, no animation.
   */
  function marqueeInViewport(rig) {
    var rect = rig.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function setupCollectionMarquee(rig) {
    if (!rig || rig.getAttribute('data-fc-marquee-init') === 'true') return true;
    var row = rig.querySelector('.fc-collection-marquee__row');
    var group = rig.querySelector('.fc-collection-marquee__group');
    if (!row || !group || REDUCED) return true;

    var groupWidth = group.getBoundingClientRect().width || group.offsetWidth;
    if (!groupWidth) return false;

    var groups = row.querySelectorAll('.fc-collection-marquee__group');
    if (groups.length < 2) {
      var clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      row.appendChild(clone);
    }

    var vw = rig.offsetWidth || window.innerWidth;
    var safety = 0;
    while (group.offsetWidth > 0 && row.scrollWidth < vw * 1.25 && safety < 6) {
      var fill = group.cloneNode(true);
      fill.setAttribute('aria-hidden', 'true');
      row.appendChild(fill);
      safety += 1;
    }

    groupWidth = group.getBoundingClientRect().width || group.offsetWidth;
    if (!groupWidth) return false;

    var speed = parseFloat(rig.getAttribute('data-fc-marquee-speed')) || 56;
    var dur = groupWidth / speed;
    if (!dur || dur < 1) dur = 18;

    gsap.set(row, { x: 0, force3D: true });
    row.style.animation = 'none';

    var tween = gsap.to(row, {
      x: -groupWidth,
      duration: dur,
      ease: 'none',
      repeat: -1,
      force3D: true,
    });

    tween.play();

    /* No hover interaction by design — the marquee runs at constant
       speed under the cursor. Off-screen pause below is perf-only. */

    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.create({
        trigger: rig,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: function () { tween.play(); },
        onEnterBack: function () { tween.play(); },
        onLeave: function () { tween.pause(); },
        onLeaveBack: function () { tween.pause(); },
      });
      if (!marqueeInViewport(rig)) tween.pause();
    }

    rig.setAttribute('data-fc-marquee-init', 'true');
    return true;
  }

  function initCollectionMarquee(root) {
    if (typeof gsap === 'undefined') return;
    var scope = root && root.querySelectorAll ? root : document;
    var rigs = scope.querySelectorAll('[data-fc-marquee]:not([data-fc-marquee-init])');
    if (!rigs.length) return;

    var pending = [];
    Array.prototype.forEach.call(rigs, function (rig) {
      if (!setupCollectionMarquee(rig)) pending.push(rig);
    });

    if (!pending.length) return;

    function retryPending() {
      var still = [];
      pending.forEach(function (rig) {
        if (!setupCollectionMarquee(rig)) still.push(rig);
      });
      pending = still;
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
    }

    requestAnimationFrame(function () {
      requestAnimationFrame(retryPending);
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(retryPending);
    }
    window.addEventListener('load', retryPending, { once: true });
  }

  /**
   * Collection landing — editorial scroll motion beyond generic .fc-reveal.
   * Flex splits (spread-style), photo-plate parallax, staggered headline lines.
   */
  function initCollectionFlexSections() {
    if (typeof ScrollTrigger === 'undefined') return;
    var sections = document.querySelectorAll('[data-fc-flex]');
    if (!sections.length) return;

    Array.prototype.forEach.call(sections, function (section) {
      var flip = section.getAttribute('data-flip') === 'true';
      var text = section.querySelector('.fc-collection-flex__copy');
      var media = section.querySelector('.fc-collection-flex__media');

      if (REDUCED) {
        if (text) gsap.set(text, { autoAlpha: 1, x: 0 });
        if (media) gsap.set(media, { autoAlpha: 1, scale: 1 });
        return;
      }

      if (text) {
        gsap.fromTo(
          text,
          Object.assign({}, fcTextHidden(0), { x: flip ? 28 : -28 }),
          Object.assign({}, fcTextVisible(), {
            x: 0,
            duration: 0.95,
            ease: FC_EASE,
            onComplete: fcRevealComplete(text),
            scrollTrigger: {
              trigger: section,
              start: 'top 82%',
              once: true,
              invalidateOnRefresh: true,
            },
          })
        );
      }
      if (media) {
        gsap.fromTo(
          media,
          fcImageHidden(0, FC_BLUR_IMAGE, 1.04),
          Object.assign({}, fcImageVisible(), {
            duration: 1.05,
            ease: FC_EASE,
            delay: 0.08,
            onComplete: fcRevealComplete(media),
            scrollTrigger: {
              trigger: section,
              start: 'top 82%',
              once: true,
              invalidateOnRefresh: true,
            },
          })
        );
      }
    });
  }

  function initCollectionPhotoPlates() {
    if (REDUCED || typeof ScrollTrigger === 'undefined') return;
    var plates = document.querySelectorAll('[data-fc-photo-plate]');
    if (!plates.length) return;

    /* Desktop-only for the same reason as initMediaParallax: no
       scrubbed scroll-linked work at phone widths. */
    var mm = gsap.matchMedia();
    mm.add('(min-width: 1024px)', function () {
      Array.prototype.forEach.call(plates, function (plate) {
        var img = plate.querySelector(
          '.fc-collection-mf__bg-img, .fc-collection-loc__bg-img, .fc-home-retailers__bg-img, .fc-home-lifestyle__bg-img, .fc-home-loyalty__bg-img'
        );
        if (!img) return;
        gsap.fromTo(
          img,
          { yPercent: 4 },
          {
            yPercent: -6,
            ease: 'none',
            scrollTrigger: {
              trigger: plate,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.2,
            },
          }
        );
      });
    });
  }

  /** Lifestyle break — gentle scale/fade when the plate enters view. */
  function initHomeLifestyleBreak() {
    if (REDUCED || typeof ScrollTrigger === 'undefined') return;
    var sections = document.querySelectorAll('[data-fc-home-lifestyle]');
    if (!sections.length) return;

    Array.prototype.forEach.call(sections, function (section) {
      var img = section.querySelector('.fc-home-lifestyle__bg-img');
      if (!img) return;

      ScrollTrigger.create({
        trigger: section,
        start: 'top 88%',
        once: true,
        invalidateOnRefresh: true,
        onEnter: function () {
          gsap.fromTo(
            img,
            { autoAlpha: 0, scale: 1.04, filter: fcBlurFrom(8) },
            {
              autoAlpha: 1,
              scale: 1,
              filter: fcBlurTo(),
              duration: 1.15,
              ease: FC_EASE,
              onComplete: fcRevealComplete(img),
            }
          );
        },
      });
    });
  }

  /** Stagger serif headline rows (line 1, then line 2). */
  function initCollectionHeadlineReveals() {
    if (REDUCED || typeof ScrollTrigger === 'undefined') return;
    var headlines = document.querySelectorAll('[data-fc-headline-reveal]');
    if (!headlines.length) return;

    Array.prototype.forEach.call(headlines, function (headline) {
      if (headline.hasAttribute('data-fc-headline-init')) return;
      if (
        headline.closest('[data-fc-split-reveal], [data-fc-home-locator], [data-fc-home-retailers]')
      ) {
        return;
      }

      var lines = fcHeadlineLines(headline);
      if (!lines.length) return;

      headline.setAttribute('data-fc-headline-init', '');

      gsap.set(headline, { autoAlpha: 1 });
      gsap.set(lines, fcTextHidden(24));
      gsap.to(lines, Object.assign({}, fcTextVisible(), {
        duration: 0.95,
        ease: FC_EASE,
        stagger: 0.1,
        onComplete: function () {
          lines.forEach(function (line) {
            fcClearFilter(line);
            if (line && line.style) line.style.willChange = 'auto';
          });
        },
        scrollTrigger: {
          trigger: headline,
          start: 'top 86%',
          once: true,
          invalidateOnRefresh: true,
        },
      }));
    });
  }

  /** Intro blocks: eyebrow → deck with short stagger (Two Forms, etc.). */
  function initCollectionIntroStaggers() {
    if (typeof ScrollTrigger === 'undefined') return;
    var intros = document.querySelectorAll('[data-fc-intro]');
    if (!intros.length) return;

    Array.prototype.forEach.call(intros, function (intro) {
      var children = intro.querySelectorAll('[data-fc-intro-child]');
      if (!children.length) return;

      if (REDUCED) {
        gsap.set(children, { autoAlpha: 1, y: 0 });
        return;
      }

      gsap.set(children, fcTextHidden(20));
      gsap.to(children, Object.assign({}, fcTextVisible(), {
        duration: 0.9,
        ease: FC_EASE,
        stagger: 0.1,
        onComplete: function () {
          children.forEach(fcClearFilter);
        },
        scrollTrigger: {
          trigger: intro,
          start: 'top 85%',
          once: true,
          invalidateOnRefresh: true,
        },
      }));
    });
  }

  /**
   * Ethos (compact brand statement) — staggered blur-fade.
   * Eyebrow → headline lines → body, then graphic settles in.
   */
  function initEthosStatement() {
    if (typeof ScrollTrigger === 'undefined') return;
    var sections = document.querySelectorAll('[data-fc-ethos]');
    if (!sections.length) return;

    Array.prototype.forEach.call(sections, function (section) {
      var copyItems = section.querySelectorAll('[data-fc-ethos-copy]');
      var graphic = section.querySelector('[data-fc-ethos-graphic]');

      if (REDUCED) {
        copyItems.forEach(function (el) { gsap.set(el, { autoAlpha: 1, y: 0, filter: 'none' }); });
        if (graphic) gsap.set(graphic, { autoAlpha: 1, y: 0, scale: 1, filter: 'none' });
        return;
      }

      var tl = gsap.timeline({
        defaults: { ease: FC_EASE },
        scrollTrigger: {
          trigger: section,
          start: 'top 78%',
          once: true,
          invalidateOnRefresh: true,
        },
      });

      if (copyItems.length) {
        gsap.set(copyItems, fcTextHidden(20));
        tl.to(copyItems, Object.assign({}, fcTextVisible(), {
          duration: 0.95,
          stagger: 0.1,
          onComplete: function () {
            copyItems.forEach(fcClearFilter);
          },
        }), 0);
      }

      if (graphic) {
        gsap.set(graphic, fcImageHidden(28, FC_BLUR_IMAGE, 0.96));
        tl.to(graphic, Object.assign({}, fcImageVisible(), {
          duration: 1.05,
          onComplete: fcRevealComplete(graphic),
        }), 0.22);
      }
    });
  }

  /** Locator — headline cascade, subtext, embed plate, footer link. */
  function initHomeLocator() {
    if (typeof ScrollTrigger === 'undefined') return;
    var sections = document.querySelectorAll('[data-fc-home-locator]');
    if (!sections.length) return;

    Array.prototype.forEach.call(sections, function (section) {
      var headline = section.querySelector('[data-fc-headline-reveal]');
      var subtext = section.querySelector('[data-fc-locator-copy]');
      var embed = section.querySelector('[data-fc-locator-embed]');
      var footer = section.querySelector('[data-fc-locator-footer]');

      if (REDUCED) {
        if (headline) gsap.set(headline, { autoAlpha: 1 });
        fcResetMotionTargets(fcHeadlineLines(headline));
        fcResetMotionTargets([subtext, embed, footer]);
        return;
      }

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top 82%',
          once: true,
          invalidateOnRefresh: true,
        },
      });

      var t = 0;
      if (headline) {
        t = fcAnimateHeadlineInTimeline(tl, headline, t, { y: 24, tail: 0.22 });
      }
      if (subtext) {
        gsap.set(subtext, fcTextHidden(20));
        tl.to(
          subtext,
          Object.assign({}, fcTextVisible(), {
            duration: 0.85,
            onComplete: fcRevealComplete(subtext),
          }),
          t
        );
        t += 0.18;
      }
      if (embed) {
        gsap.set(embed, { autoAlpha: 0, y: 28, scale: 0.985 });
        tl.to(
          embed,
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: FC_EASE,
            onComplete: fcRevealComplete(embed),
          },
          t
        );
        t += 0.28;
      }
      if (footer) {
        gsap.set(footer, fcTextHidden(16));
        tl.to(
          footer,
          Object.assign({}, fcTextVisible(), {
            duration: 0.8,
            onComplete: fcRevealComplete(footer),
          }),
          t
        );
      }
    });
  }

  /** Retailers CTA — staggered copy over the full-bleed plate. */
  function initHomeRetailers() {
    if (typeof ScrollTrigger === 'undefined') return;
    var sections = document.querySelectorAll('[data-fc-home-retailers]');
    if (!sections.length) return;

    Array.prototype.forEach.call(sections, function (section) {
      var copyItems = section.querySelectorAll('[data-fc-retailers-copy]');
      var scrim = section.querySelector('.fc-home-retailers__scrim');

      if (REDUCED) {
        fcResetMotionTargets(copyItems);
        if (scrim) gsap.set(scrim, { autoAlpha: 1 });
        return;
      }

      if (scrim) gsap.set(scrim, { autoAlpha: 0.85 });

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top 78%',
          once: true,
          invalidateOnRefresh: true,
        },
      });

      if (scrim) {
        tl.to(scrim, { autoAlpha: 1, duration: 0.9, ease: FC_EASE }, 0);
      }

      if (copyItems.length) {
        gsap.set(copyItems, fcTextHidden(22));
        tl.to(
          copyItems,
          Object.assign({}, fcTextVisible(), {
            duration: 0.9,
            stagger: 0.1,
            onComplete: function () {
              copyItems.forEach(fcClearFilter);
            },
          }),
          0.12
        );
      }
    });
  }

  function initEditorialScrollMotion() {
    initCollectionHeadlineReveals();
    initCollectionIntroStaggers();
    initCollectionFlexSections();
  }

  function init() {
    if (isDesignMode()) {
      document.querySelectorAll('.fc-hero-anim').forEach(function (el) {
        gsap.set(el, { autoAlpha: 1, y: 0, clearProps: 'transform,opacity,visibility' });
      });
      document.querySelectorAll('.fc-collection-hero__product-inner').forEach(function (el) {
        gsap.set(el, { autoAlpha: 1, y: 0, scale: 1, filter: 'none', clearProps: 'all' });
      });
    }

    var collectionHero = document.querySelector(
      '[data-section-type="fc-collection-hero"], #fc-collection-hero'
    );
    if (collectionHero) {
      initCollectionHeroChoreography(collectionHero);
    } else {
      initHeroEntrance();
      initHeroPhotoBreathe();
      initHeroFocusReveal();
    }

    initMobileMenu();
    initScrollReveals();
    initStaggerGroups();
    initSectionDividers();
    initSpreadChoreography();
    initSpreadEyebrowRules();
    initMediaParallax();
    initSplitReveal();
    initMissionReveal();
    initEthosStatement();
    initHomeLocator();
    initHomeRetailers();
    initHomeLifestyleBreak();
    initCollectionPhotoPlates();
    initMenuDrawerMotion();
    initCollectionProductCards();
    initCollectionMarquee();
    initEditorialScrollMotion();
    initImageLoadRefresh();
    requestAnimationFrame(refreshScrollMotion);
    window.addEventListener('load', refreshScrollMotion, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    handleHeroSectionEvent(event);
    if (event.detail && event.detail.sectionId) {
      var section = document.querySelector('[data-section-id="' + event.detail.sectionId + '"]');
      if (section && section.hasAttribute('data-fc-marquee')) {
        section.removeAttribute('data-fc-marquee-init');
        initCollectionMarquee(section.parentElement || document);
      }
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        handleHeroSectionEvent(event);
      });
    });
  });

  document.addEventListener('shopify:section:select', function (event) {
    handleHeroSectionEvent(event);
  });

  document.addEventListener('shopify:block:select', function (event) {
    handleHeroSectionEvent(event);
  });
})();
